import { useEffect } from 'react';
import type { Virtualizer } from '@tanstack/react-virtual';
import { _isNil, _notNil } from '@/littledash';
import {
  type DataTableCellSelectionChangeEvent,
  DataTableEvent,
  type DataTableMoveColumnEvent,
  type DataTableService,
  type DataTableStateChangeEvent,
} from '../DataTable.model';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';

interface UseAutoScrollOptions {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  dataTableService: DataTableService;
  pinnedColumnCount: number;
}

const validateDimensions = (
  cellHeight: number,
  cellWidth: number,
  coordinate: { row: number; column: number }
): boolean => {
  if (cellHeight <= 0 || !Number.isFinite(cellHeight)) {
    ExceptionHandler.captureException(
      new InVivoError('Invalid cellHeight for auto-scroll calculation', {
        slug: 'datatable-auto-scroll-validation',
        level: 'warning',
        context: {
          cellHeight: String(cellHeight),
          cellWidth: String(cellWidth),
          row: String(coordinate.row),
          column: String(coordinate.column),
        },
      })
    );
    return false;
  }

  if (cellWidth <= 0 || !Number.isFinite(cellWidth)) {
    ExceptionHandler.captureException(
      new InVivoError('Invalid cellWidth for auto-scroll calculation', {
        slug: 'datatable-auto-scroll-validation',
        level: 'warning',
        context: {
          cellHeight: String(cellHeight),
          cellWidth: String(cellWidth),
          row: String(coordinate.row),
          column: String(coordinate.column),
        },
      })
    );
    return false;
  }

  return true;
};

export const useAutoScroll = ({
  rowVirtualizer,
  columnVirtualizer,
  dataTableService,
  pinnedColumnCount,
}: UseAutoScrollOptions): void => {
  useEffect(() => {
    const abortController = new AbortController();

    const handleSelectionChange = (event: DataTableCellSelectionChangeEvent): void => {
      try {
        const { scrollTo, activate, selection } = event.detail;

        const scrollElement = rowVirtualizer.scrollElement;
        if (_isNil(scrollElement)) {
          return;
        }

        const { from } = selection;
        const { cellHeight, cellWidth } = dataTableService.dimensions;

        // Validate dimensions before calculations
        if (!validateDimensions(cellHeight, cellWidth, from)) {
          return;
        }

        // If scrollTo is explicitly requested, snap the cell to the center
        if (scrollTo) {
          const scrollableColumnIndex = from.column - pinnedColumnCount;
          rowVirtualizer.scrollToIndex(from.row, { align: 'center' });
          if (scrollableColumnIndex >= 0) {
            columnVirtualizer.scrollToIndex(scrollableColumnIndex, { align: 'center' });
          }
          return;
        }

        // Skip auto-scroll for cell activation - user has already positioned viewport via click
        if (activate) {
          return;
        }

        // Calculate visible viewport boundaries from scroll position
        const visibleRowStart = Math.floor(scrollElement.scrollTop / cellHeight);
        const visibleRowEnd = visibleRowStart + Math.ceil(scrollElement.clientHeight / cellHeight) - 1;

        const visibleColumnStart = Math.floor(scrollElement.scrollLeft / cellWidth);
        const visibleColumnEnd = visibleColumnStart + Math.ceil(scrollElement.clientWidth / cellWidth) - 1;

        // Adjust for pinned columns (selection uses absolute indices, virtualizer uses relative)
        const scrollableColumnIndex = from.column - pinnedColumnCount;

        // Check if selection is at viewport edges
        const needsVerticalScroll = from.row <= visibleRowStart || from.row >= visibleRowEnd;
        const needsHorizontalScroll =
          scrollableColumnIndex >= 0 &&
          (scrollableColumnIndex <= visibleColumnStart || scrollableColumnIndex >= visibleColumnEnd);

        if (needsVerticalScroll) {
          rowVirtualizer.scrollToIndex(from.row, { align: 'auto' });
        }

        if (needsHorizontalScroll) {
          columnVirtualizer.scrollToIndex(scrollableColumnIndex, { align: 'auto' });
        }
      } catch (error) {
        ExceptionHandler.captureException(
          new InVivoError('Auto-scroll handler failed', {
            cause: error,
            slug: 'datatable-auto-scroll-handler',
            level: 'warning',
            context: {
              row: `${event.detail.selection.from.row}`,
              column: `${event.detail.selection.from.column}`,
              scrollTo: `${event.detail.scrollTo}`,
              activate: `${event.detail.activate}`,
            },
          })
        );
      }
    };

    const handleMoveColumn = (event: DataTableMoveColumnEvent): void => {
      // Extract columnId early for safe error reporting
      const columnId = event.detail?.columnId;

      try {
        if (_isNil(columnId)) {
          return;
        }

        const scrollElement = columnVirtualizer.scrollElement;
        if (_isNil(scrollElement)) {
          return;
        }

        const leafColumns = dataTableService.leafColumns;
        const columnGroupingData = dataTableService.getColumnGroupingData();

        let targetColumnIndex: number | null = null;

        // First, check if this is a parent/grouped column
        if (_notNil(columnGroupingData)) {
          const group = columnGroupingData.columnGroups.find((g) => g.parentColumn.id === columnId);
          if (_notNil(group)) {
            targetColumnIndex = group.startIndex;
          }
        }

        // If not a parent column, search leafColumns
        if (targetColumnIndex === null) {
          const leafIndex = leafColumns.findIndex((col) => col.id === columnId);
          if (leafIndex >= 0) {
            targetColumnIndex = leafIndex;
          }
        }

        if (targetColumnIndex === null) {
          return;
        }

        const scrollableColumnIndex = targetColumnIndex - pinnedColumnCount;
        if (scrollableColumnIndex < 0) {
          return;
        }

        requestAnimationFrame(() => {
          columnVirtualizer.scrollToIndex(scrollableColumnIndex, { align: 'start', behavior: 'smooth' });
        });
      } catch (error) {
        ExceptionHandler.captureException(
          new InVivoError('Column move auto-scroll handler failed', {
            cause: error,
            slug: 'datatable-column-move-auto-scroll',
            level: 'warning',
            context: {
              columnId,
            },
          })
        );
      }
    };

    const handleTableStateChange = (event: DataTableStateChangeEvent): void => {
      try {
        const { type, ids } = event.detail;

        if (!type.has('columns') || ids.size === 0) {
          return;
        }

        const scrollElement = columnVirtualizer.scrollElement;
        if (_isNil(scrollElement)) {
          return;
        }

        // Check if this is a full-column-set update (e.g., from moveColumn)
        // If so, skip this handler as the MoveColumn handler will scroll appropriately
        const totalColumnCount = dataTableService.columns.length;
        if (ids.size === totalColumnCount) {
          return;
        }

        const leafColumns = dataTableService.leafColumns;
        const columnGroupingData = dataTableService.getColumnGroupingData();

        const newColumnIndices: Array<number> = [];

        if (_notNil(columnGroupingData)) {
          ids.forEach((columnId) => {
            const group = columnGroupingData.columnGroups.find((g) => g.parentColumn.id === columnId);
            if (_notNil(group)) {
              newColumnIndices.push(group.startIndex);
            }
          });
        }

        if (newColumnIndices.length === 0) {
          leafColumns.forEach((col, index) => {
            if (ids.has(col.id)) {
              newColumnIndices.push(index);
            }
          });
        }

        if (newColumnIndices.length === 0) {
          return;
        }

        const lastNewColumnIndex = Math.max(...newColumnIndices);
        const scrollableColumnIndex = lastNewColumnIndex - pinnedColumnCount;

        if (scrollableColumnIndex < 0) {
          return;
        }

        requestAnimationFrame(() => {
          columnVirtualizer.scrollToIndex(scrollableColumnIndex, { align: 'end' });
        });
      } catch (error) {
        ExceptionHandler.captureException(
          new InVivoError('Column addition auto-scroll handler failed', {
            cause: error,
            slug: 'datatable-column-add-auto-scroll',
            level: 'warning',
            context: {
              eventType: Array.from(event.detail.type).join(','),
              columnIds: Array.from(event.detail.ids).join(','),
            },
          })
        );
      }
    };

    dataTableService.subscribe(DataTableEvent.CellSelectionChange, handleSelectionChange, abortController.signal);
    dataTableService.subscribe(DataTableEvent.TableStateChange, handleTableStateChange, abortController.signal);
    dataTableService.subscribe(DataTableEvent.MoveColumn, handleMoveColumn, abortController.signal);

    return () => {
      abortController.abort('teardown');
    };
  }, [rowVirtualizer, columnVirtualizer, dataTableService, pinnedColumnCount]);
};
